import React from 'react';
import { Stage, Layer, Line, Circle, Shape } from 'react-konva';
import Dar from './dar.jsx';
import Panjere from './panjere.jsx';
import Bazsho from './bazsho.jsx';
import './planMagnifier.scss';

const MAGNIFIER_SIZE = 168;
const MAGNIFIER_ZOOM = 0.85; // زوم‌اوت‌تر از حالت واقعی، تا فضای بیشتری از اطراف نقطه لمس دیده شود
const MINI_GRID_SIZE = 50; // هم‌راستا با گرید صفحه اصلی، صرفاً برای حس مقیاس در ذره‌بین

function drawMiniGrid(ctx, pointer) {
  const range = 400;
  const startX = Math.floor((pointer.x - range) / MINI_GRID_SIZE) * MINI_GRID_SIZE;
  const endX = Math.ceil((pointer.x + range) / MINI_GRID_SIZE) * MINI_GRID_SIZE;
  const startY = Math.floor((pointer.y - range) / MINI_GRID_SIZE) * MINI_GRID_SIZE;
  const endY = Math.ceil((pointer.y + range) / MINI_GRID_SIZE) * MINI_GRID_SIZE;
  ctx.beginPath();
  ctx.strokeStyle = 'rgba(0, 0, 0, 0.055)';
  ctx.lineWidth = 1;
  for (let x = startX; x <= endX; x += MINI_GRID_SIZE) {
    ctx.moveTo(x, startY);
    ctx.lineTo(x, endY);
  }
  for (let y = startY; y <= endY; y += MINI_GRID_SIZE) {
    ctx.moveTo(startX, y);
    ctx.lineTo(endX, y);
  }
  ctx.stroke();
}

function PlanMagnifier({
  pointer,
  walls,
  doors,
  windows,
  openings,
  wallThickness,
  doorWidth,
  windowWidth,
  openingWidth,
  getItemPlacement,
  angleDeg,
  distanceLabel,
  connected,
  junction,
}) {
  if (!pointer) return null;

  const hasAngle = angleDeg !== null && angleDeg !== undefined;
  const hasDistance = !connected && !junction && !!distanceLabel;
  const showHud = hasAngle || hasDistance || connected || !!junction;

  return (
    <div className="plan-magnifier">
      <div className="plan-magnifier__glass">
        <Stage width={MAGNIFIER_SIZE} height={MAGNIFIER_SIZE}>
          <Layer
            x={MAGNIFIER_SIZE / 2}
            y={MAGNIFIER_SIZE / 2}
            scaleX={MAGNIFIER_ZOOM}
            scaleY={MAGNIFIER_ZOOM}
            offsetX={pointer.x}
            offsetY={pointer.y}
          >
            <Shape
              sceneFunc={(ctx, shape) => {
                drawMiniGrid(ctx, pointer);
                ctx.fillStrokeShape(shape);
              }}
              listening={false}
              perfectDrawEnabled={false}
            />

            {walls.map((wall) => (
              <Line
                key={wall.id}
                points={[wall.x1, wall.y1, wall.x2, wall.y2]}
                stroke="#1c1c1c"
                strokeWidth={wallThickness}
                lineCap="square"
                perfectDrawEnabled={false}
              />
            ))}

            {doors.map((door) => {
              const wall = walls.find((w) => w.id === door.wallId);
              if (!wall) return null;
              const placement = getItemPlacement(wall, door.ratio, doorWidth);
              return (
                <Dar
                  key={door.id}
                  x={placement.x}
                  y={placement.y}
                  length={doorWidth}
                  angleDeg={placement.angleDeg}
                  thickness={wallThickness}
                  flip={door.facing === 'in'}
                />
              );
            })}

            {windows.map((win) => {
              const wall = walls.find((w) => w.id === win.wallId);
              if (!wall) return null;
              const placement = getItemPlacement(wall, win.ratio, windowWidth);
              return <Panjere key={win.id} x={placement.x} y={placement.y} length={windowWidth} angleDeg={placement.angleDeg} thickness={wallThickness} />;
            })}

            {(openings || []).map((opening) => {
              const wall = walls.find((w) => w.id === opening.wallId);
              if (!wall) return null;
              const placement = getItemPlacement(wall, opening.ratio, openingWidth);
              return <Bazsho key={opening.id} x={placement.x} y={placement.y} length={openingWidth} angleDeg={placement.angleDeg} thickness={wallThickness} />;
            })}
          </Layer>
        </Stage>

        {/* رتیکل لمس — نقطه لمس‌شده همیشه دقیقاً وسط ذره‌بین قرار می‌گیرد */}
        <div className="plan-magnifier__reticle">
          <span className="plan-magnifier__reticle-line plan-magnifier__reticle-line--h" />
          <span className="plan-magnifier__reticle-line plan-magnifier__reticle-line--v" />
          <span className="plan-magnifier__reticle-ring" />
          <span className="plan-magnifier__reticle-dot" />
        </div>

        {showHud && (
          <div className="plan-magnifier__hud">
            <div className="plan-magnifier__pills">
              {hasAngle && (
                <span className="plan-magnifier__pill plan-magnifier__pill--angle">
                  <span className="plan-magnifier__pill-icon">∠</span>
                  {Math.round(angleDeg).toLocaleString('fa-IR')}°
                </span>
              )}
              {connected && (
                <span className="plan-magnifier__pill plan-magnifier__pill--connected">
                  <span className="plan-magnifier__pill-icon">✓</span>
                  متصل
                </span>
              )}
              {junction && (
                <>
                  <span className="plan-magnifier__pill plan-magnifier__pill--distance">
                    <span className="plan-magnifier__pill-icon">⟵</span>
                    ابتدا {junction.toStart}
                  </span>
                  <span className="plan-magnifier__pill plan-magnifier__pill--distance">
                    <span className="plan-magnifier__pill-icon">⟶</span>
                    انتها {junction.toEnd}
                  </span>
                </>
              )}
              {hasDistance && (
                <span className="plan-magnifier__pill plan-magnifier__pill--distance">
                  <span className="plan-magnifier__pill-icon">↔</span>
                  {distanceLabel}
                </span>
              )}
            </div>
          </div>
        )}

        <div className="plan-magnifier__vignette" />
      </div>
    </div>
  );
}

export default PlanMagnifier;